/**
 * Add or change ONE transition, from one of its states - issue #50.
 *
 * The person is ON a state and says which way the edge goes - out of it (To),
 * or into it (From) - and the other state. Where the edge is declared is not
 * asked: it lives in the innermost state enclosing both, and the server
 * decides that. So only the other states that rule can accept are offered;
 * the rest could only be refused.
 *
 * On the canvas, where an edge is drawn between two boxes, both ends are
 * fixed and only the rest is asked.
 */

import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { ApiError, api } from '../../api'
import type { EntityRow, TouchingTransition, TransitionEdit } from '../../schema'
import { useSchema } from '../../state/AppData'
import ElementPicker from '../common/ElementPicker'
import { useElements } from '../common/elements'
import { canJoin, directionOf, offeredAsDecider, stateName, stateRefs } from './state-authoring'
import type { Direction } from './state-authoring'

export default function TransitionDialog({
  state,
  states,
  editing,
  other: fixedOther,
  direction: fixedDirection,
  onClose,
  onSaved,
}: {
  /** The state the person is on. */
  state: EntityRow
  /** Every state in the model. */
  states: EntityRow[]
  /** The edge being changed, as a read reported it; absent to add one. */
  editing?: TouchingTransition
  /** Both ends fixed, as on the canvas: the other end and which way it goes. */
  other?: string
  direction?: Direction
  onClose: () => void
  onSaved: () => void
}) {
  const schema = useSchema()
  const parts = useElements(schema, 'parts')
  const [direction, setDirection] = useState<Direction>(
    fixedDirection ?? (editing ? directionOf(editing, state.id) : 'to'))
  const [other, setOther] = useState(
    fixedOther ?? (editing ? (editing.from === state.id ? editing.to : editing.from) : ''))
  const [trigger, setTrigger] = useState(editing?.trigger ?? '')
  const [guard, setGuard] = useState(editing?.guard ?? '')
  const [effect, setEffect] = useState(editing?.effect ?? '')
  const [decider, setDecider] = useState(editing?.decided_by ?? '')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')

  const refs = stateRefs(states)
  const here = String(state.qualified_name ?? '')
  const deciderIds = new Set(parts.rows.map((row) => row.id))
  const deciderBroken = parts.complete && Boolean(decider) && !deciderIds.has(decider)
  const bothFixed = fixedOther !== undefined

  const save = () => {
    const [source, target] = direction === 'to' ? [state.id, other] : [other, state.id]
    const edit: TransitionEdit = {
      source, target, trigger, guard, effect, decided_by: decider,
      // Kept as written: a change must not rename an edge or respell its effect.
      ...(editing ? {
        identifier: editing.identifier ?? '',
        name: editing.declared_name ?? '',
        ...(editing.effect_keyword === undefined || !effect ? {} : { effect_keyword: editing.effect_keyword }),
      } : {}),
    }
    setBusy(true)
    setFailure('')
    ;(editing ? api.changeTransition(editing, edit) : api.addTransition(edit))
      .then(() => onSaved())
      .catch((error: ApiError) => setFailure(error.message))
      .finally(() => setBusy(false))
  }

  const title = editing ? 'Change transition' : 'New transition'

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {title} {direction === 'to' ? 'out of' : 'into'} {stateName(state)}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}
          {!bothFixed && (
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={direction}
                onChange={(_event, next: Direction | null) => next && setDirection(next)}
                aria-label="Direction"
              >
                <ToggleButton value="to">To</ToggleButton>
                <ToggleButton value="from">From</ToggleButton>
              </ToggleButtonGroup>
              <ElementPicker
                rows={states}
                // Only the states a transition with this one can be declared
                // for. The same state is allowed: a self-transition is
                // declared in its parent.
                where={(row) => canJoin(refs, here, String(row.qualified_name ?? ''))}
                describe={(row, value) => ({
                  value,
                  label: stateName(row),
                  code: row.name && row.name !== row.id ? row.id : undefined,
                  detail: String(row.qualified_name ?? ''),
                })}
                show="label"
                strict="state"
                value={other || null}
                onChange={setOther}
                label={direction === 'to' ? 'To state' : 'From state'}
                required
                fullWidth
              />
            </Stack>
          )}
          <TextField
            label="Trigger"
            size="small"
            value={trigger}
            onChange={(event) => setTrigger(event.target.value)}
            placeholder="accept …"
            helperText="The signal that takes the edge, if any"
          />
          <TextField
            label="Guard"
            size="small"
            value={guard}
            onChange={(event) => setGuard(event.target.value)}
            placeholder="if …"
          />
          <TextField
            label="Effect"
            size="small"
            value={effect}
            onChange={(event) => setEffect(event.target.value)}
            placeholder="do …"
          />
          <ElementPicker
            rows={parts.rows}
            where={offeredAsDecider}
            strict={parts.complete ? 'part' : false}
            value={decider || null}
            onChange={setDecider}
            label="Decided by"
            helperText="The part that decides to take it"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={busy || !other || deciderBroken}
        >
          {busy ? 'Saving…' : editing ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
